import { createAction, createSlice } from '@reduxjs/toolkit';
import cloneDeep from 'lodash-es/cloneDeep';
import keyBy from 'lodash-es/keyBy';

import { defaultDesignerState } from '@/store/_setup/initialState';
import { LogoutAction } from '@/store/user/actions/LogoutAction';

export interface Design {
  id: string;
  name: string;
  colors: BlogDesignColors;
  background: BlogDesignBackground;
  layout: BlogDesignLayout;
  header: BlogDesignHeader;
  fonts: BlogDesignFonts;
}

export interface DesignerState {
  designerOn: boolean;
  designerCollapsed: boolean;
  designs: Record<string, Design>;
  draftDesign: Partial<Design>;
}

export interface BlogDesignColors {
  borders: string;
  dividers: string;
  headers: string;
  meta: string;
  links: string;
  text: string;
  offtop: string;
  blocks: string;
  background: string;
  elements: string;
  elementsBack: string;
  accent: string;
  buttons: string;
}

export interface BlogDesignBackground {
  enable: boolean;
  url: string;
  size: string;
  position: string;
  repeat: string;
  attachment: string;
}

export interface BlogDesignLayout {
  maxWidth: number;
  layout: number;
  align: string;
  blocks: boolean;
  epigraph: boolean;
}

export interface BlogDesignHeaderFont {
  family: string;
  size: number;
  style: string;
  weight: string;
  shadow: boolean;
}

export interface BlogDesignHeaderImage {
  enable: boolean;
  url: string;
  size: string;
  repeat: string;
  position: string;
  attachment: string;
}

export interface BlogDesignHeader {
  background: string;
  color: string;
  font: BlogDesignHeaderFont;
  image: BlogDesignHeaderImage;
}

export interface BlogDesignFont {
  family: string;
  size: number;
  weight: string;
  style: string;
}

export interface BlogDesignEntriesFont extends BlogDesignFont {
  height: number;
  align: string;
}

export interface BlogDesignFonts {
  headers: BlogDesignFont;
  entries: BlogDesignEntriesFont;
  elements: BlogDesignFont;
  menu: BlogDesignFont;
}

// Actions
export const addDesignAction = createAction<Design>('designer/ADD_DESIGN');
export const clearDesignAction = createAction('designer/CLEAR_DESIGNS');
export const collapseDesignerAction = createAction('designer/COLLAPSE');
export const deleteDesignAction = createAction<string>(
  'designer/DELETE_DESIGN'
);
export const endEditingAction = createAction('designer/END_EDITING');
export const expandDesignerAction = createAction('designer/EXPAND');
export const startEditingAction = createAction<Partial<Design>>(
  'designer/START_EDITING'
);
export const storeDesignsAction = createAction<Design[]>(
  'designer/STORE_DESIGNS'
);
export const updateDesignAction =
  createAction<Partial<Design>>('designer/UPDATE');

const designerSlice = createSlice({
  name: 'designer',
  initialState: defaultDesignerState as DesignerState,
  reducers: {},
  // Use extraReducers to handle all actions with custom type strings
  extraReducers: builder => {
    builder
      .addCase(addDesignAction, (state, action) => {
        state.designs[action.payload.id] = action.payload;
      })
      .addCase(storeDesignsAction, (state, action) => {
        state.designs = keyBy(action.payload, 'id');
      })
      .addCase(deleteDesignAction, (state, action) => {
        delete state.designs[action.payload];
      })
      .addCase(collapseDesignerAction, state => {
        state.designerCollapsed = true;
      })
      .addCase(startEditingAction, (state, action) => {
        state.draftDesign = action.payload;
        state.designerOn = true;
        state.designerCollapsed = false;
      })
      .addCase(endEditingAction, state => {
        state.draftDesign = {};
        state.designerOn = false;
      })
      .addCase(expandDesignerAction, state => {
        state.designerCollapsed = false;
      })
      .addCase(updateDesignAction, (state, action) => {
        state.draftDesign = {
          ...state.draftDesign,
          ...action.payload
        };
      })
      .addCase(clearDesignAction, () => cloneDeep(defaultDesignerState))
      .addCase(LogoutAction.type, () => cloneDeep(defaultDesignerState));
  }
});

export const designerReducer = designerSlice.reducer;
